import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Zap, Calendar, CheckCircle2,
  Loader2, ArrowRight, AlertTriangle, ShieldCheck,
  Star, Gift, XCircle,
  Link2, Check
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import {
  usuarioService,
  googleService,
  pagamentoService
} from '../services/api';
import { TRIAL_PADRAO } from '../config/planos';

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingSlug, setSavingSlug] = useState(false);
  const [ativandoTrial, setAtivandoTrial] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const [slug, setSlug] = useState('');
  const [slugOriginal, setSlugOriginal] = useState('');
  const [slugStatus, setSlugStatus] = useState('idle'); // idle | checking | disponivel | indisponivel
  const [perfil, setPerfil] = useState(null);
  const debounceRef = useRef(null);

  const user = auth.currentUser;

  // LÓGICA DE ATIVAÇÃO AUTOMÁTICA VIA URL
  useEffect(() => {
    console.log("🔍 [DEBUG] useEffect da URL rodou");

    const queryParams = new URLSearchParams(location.search);
    const statusParam = queryParams.get('status');
    console.log(statusParam);

    console.log("📍 [DEBUG] Status na URL:", statusParam);
    console.log("👤 [DEBUG] Usuário logado no Onboarding:", user?.uid);

    if (statusParam === 'google_success' && user) {
      console.log("🚀 [DEBUG] Condição satisfeita! Iniciando finalizarOnboardingComTrial...");

      const finalizarOnboardingComTrial = async () => {
        setAtivandoTrial(true);
        try {
          const token = await user.getIdToken();
          console.log("🔑 [DEBUG] Token Firebase obtido com sucesso");

          const payloadTrial = {
            diasTrial: TRIAL_PADRAO.dias,
            dados: {
              nome: user.displayName || "Usuário Sincro",
              email: user.email,
              uid: user.uid,
              cpfCnpj: ""
            }
          };

          console.log("📤 [DEBUG] Enviando payload para ativarTrial:", payloadTrial);
          const res = await pagamentoService.ativarTrial(payloadTrial, token);
          console.log("✅ [DEBUG] Resposta do Servidor (Trial):", res.data);

          setTimeout(() => {
            console.log("🏁 [DEBUG] Onboarding Finalizado. Chamando onComplete...");
            onComplete();
          }, 1500);
        } catch (err) {
          console.error("❌ [DEBUG] Erro na ativação automática:", err);
          setError("Conectamos sua agenda, mas houve um erro ao ativar seu trial.");
          setTimeout(() => onComplete(), 3000);
        } finally {
          setAtivandoTrial(false);
        }
      };
      finalizarOnboardingComTrial();
    } else {
      console.log("⚠️ [DEBUG] Parâmetros de URL ou Usuário não batem para ativação automática.");
    }
  }, [location, user, onComplete]);

  const checkInitialStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [resStatus, resPerfil] = await Promise.all([
        usuarioService.verificarOnboarding(user.uid),
        usuarioService.obterPerfil(user.uid)
      ]);

      setStatus(resStatus.data);
      setPerfil(resPerfil.data);
      setSlug(resPerfil.data.slug || '');
      setSlugOriginal(resPerfil.data.slug || '');

      if (resStatus.data.completo) {
        onComplete();
      }
    } catch (err) {
      console.error("Erro ao checar onboarding:", err);
      setError("Não conseguimos carregar os dados de configuração.");
    } finally {
      setLoading(false);
    }
  }, [user.uid, onComplete]);

  useEffect(() => {
    checkInitialStatus();
  }, [checkInitialStatus]);

  const handleSlugChange = (valor) => {
    const slugFormatado = valor.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    setSlug(slugFormatado);

    clearTimeout(debounceRef.current);

    if (!slugFormatado || slugFormatado === slugOriginal) {
      setSlugStatus('idle');
      return;
    }

    setSlugStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await usuarioService.verificarSlugDisponivel(slugFormatado, user.uid);
        setSlugStatus(res.data.disponivel ? 'disponivel' : 'indisponivel');
      } catch (error) {
        setSlugStatus('idle');
      }
    }, 500);
  };

  const handleSalvarSlug = async () => {
    if (slugStatus === 'indisponivel') {
      return setError("Esse link já está em uso. Escolha outro.");
    }

    setSavingSlug(true);
    setError(null);
    try {
      const payload = {
        ...perfil,
        slug: slug
      };
      await usuarioService.atualizarPerfil(user.uid, payload);
      setSlugOriginal(slug);
      setSlugStatus('idle');
      setStep(3); // Avança para o passo da agenda do Google
    } catch (err) {
      console.error("Erro ao salvar o link de agendamento:", err);
      setError(err.response?.data?.erro || "Erro ao salvar seu link de agendamento.");
    } finally {
      setSavingSlug(false);
    }
  };

  const handlePular = () => {
    if (onSkip) onSkip();
    else onComplete();
  };

  const handleConnectGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await googleService.gerarUrlAuth(user.uid);
      window.location.href = response.data.url;
    } catch (err) {
      setError("Não foi possível iniciar a conexão com o Google.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/98 backdrop-blur-xl z-[9999] flex items-center justify-center p-5 font-sans overflow-y-auto">

      {/* Container Principal */}
      <div className="relative w-full max-w-[540px] bg-white rounded-[40px] p-6 sm:p-12 text-center shadow-[0_30px_60px_-12px_rgba(0,0,0,0.6)] border border-purple-50 animate-fade-in-up">

        {/* Banner de erro flutuante */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl mb-5 text-sm font-bold flex items-center gap-2.5 justify-center">
            <AlertTriangle size={18} className="shrink-0" />
            <span>{error}</span>
            <XCircle size={16} className="cursor-pointer ml-auto text-red-500 hover:opacity-80" onClick={() => setError(null)} />
          </div>
        )}

        {/* Linha dos Passos / Dots indicativos */}
        <div className="flex gap-2.5 justify-center mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className={`h-2.5 rounded-full transition-all duration-450 ${step === i ? 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] w-8' : 'bg-slate-200 w-2.5'}`} />
          ))}
        </div>

        {/* ================= PASSO 1: CARD DE BOAS-VINDAS ================= */}
        {step === 1 && (
          <div className="text-center">
            {/* INSERÇÃO DE MASCOTE */}
            <img src="1.png" alt="Boas-vindas" className="w-40 mx-auto mb-6 drop-shadow-lg" />

            <div className="inline-flex items-center gap-2.5 bg-orange-50 border border-orange-100 px-5 py-2.5 rounded-full mb-6">
              <Gift size={16} className="text-orange-700" fill="#c2410c" />
              <span className="text-[11px] font-black text-orange-700 uppercase tracking-wider">Passe de 7 Dias Pro Liberado!</span>
            </div>

            <div className="w-24 h-24 bg-purple-50 text-[#6D28D9] rounded-[30px] flex items-center justify-center mx-auto mb-8 -rotate-6 shadow-md shadow-purple-500/5">
              <Zap size={48} fill="currentColor" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase italic tracking-tight mb-4">Resgatar meu Bônus</h2>
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8">Seja bem-vindo. Você acaba de ganhar 7 dias de Plano Pro. Para liberar seu presente e sincronizar seus horários, basta completar os passos:</p>

            <div className="bg-slate-50 border border-slate-100 rounded-3xl p-6 mb-8 text-left">
              <div className={`flex items-center gap-3.5 mb-4 last:mb-0 font-bold text-sm uppercase tracking-wide ${(slug || status?.passos?.slug) ? 'text-green-500' : 'text-slate-400'}`}>
                <CheckCircle2 size={20} className="shrink-0" /> <span>1. Criar Link de Agendamento</span>
              </div>
              <div className={`flex items-center gap-3.5 last:mb-0 font-bold text-sm uppercase tracking-wide ${status?.passos?.google ? 'text-green-500' : 'text-slate-400'}`}>
                <CheckCircle2 size={20} className="shrink-0" /> <span>2. Sincronizar Google Agenda</span>
              </div>
            </div>

            <button className="w-full h-14 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-2xl text-xs font-black uppercase tracking-wider cursor-pointer flex items-center justify-center gap-3 transition-all duration-400 hover:opacity-90 hover:-translate-y-0.5 active:scale-98" onClick={() => setStep(2)}>
              Ativar minha Conta Agora <ArrowRight size={20} />
            </button>
            <button className="w-full bg-transparent border-none text-slate-400 text-xs font-bold mt-5 cursor-pointer hover:text-slate-600 hover:underline" onClick={handlePular}>
              Prefiro explorar primeiro, conectar depois
            </button>
          </div>
        )}

        {/* ================= PASSO 2: CRIAÇÃO DO LINK DE AGENDAMENTO ================= */}
        {step === 2 && (
          <div className="text-center animate-fade-in-up">
            {/* INSERÇÃO DE MASCOTE */}
            <img src="2.png" alt="Link de Agendamento" className="w-40 mx-auto mb-6 drop-shadow-lg" />

            <div className="w-24 h-24 bg-purple-50 text-[#6D28D9] rounded-[30px] flex items-center justify-center mx-auto mb-8 -rotate-6 shadow-md shadow-purple-500/5">
              <Link2 size={48} />
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase italic tracking-tight mb-4">Seu Link de Agendamento</h2>
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8">
              Personalize o endereço que seus pacientes usarão para agendar consultas com você diretamente, sem precisar de WhatsApp.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 bg-[#F8F7FF] border-2 border-slate-200 rounded-2xl px-4 py-1 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-[#6D28D9]/5">
                <span className="text-xs sm:text-sm font-bold text-slate-400 whitespace-nowrap">agendasincro.com.br/</span>
                <input
                  type="text"
                  className="w-full py-4 border-none bg-transparent outline-none text-base sm:text-lg font-bold text-[#1e293b] placeholder-slate-300"
                  placeholder="seu-nome-ou-clinica"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                />
                {slugStatus === 'checking' && <Loader2 size={18} className="animate-spin text-slate-400 shrink-0" />}
                {slugStatus === 'disponivel' && <Check size={18} className="text-emerald-500 shrink-0" />}
                {slugStatus === 'indisponivel' && <XCircle size={18} className="text-red-500 shrink-0" />}
              </div>

              {slugStatus === 'indisponivel' && (
                <p className="text-xs text-red-500 font-bold text-left ml-2 m-0">Esse link já está em uso. Escolha outro.</p>
              )}

              <button
                className="w-full h-14 bg-[#0f172a] text-white border-none rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all duration-400 hover:bg-[#6D28D9] hover:-translate-y-0.5 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSalvarSlug}
                disabled={savingSlug || slugStatus === 'checking' || slugStatus === 'indisponivel' || !slug}
              >
                {savingSlug ? <Loader2 className="animate-spin" size={20} /> : "Confirmar Link e Avançar"}
              </button>
            </div>

            <button className="w-full bg-transparent border-none text-slate-400 text-xs font-bold mt-6 cursor-pointer hover:text-slate-600 hover:underline" onClick={() => setStep(3)}>
              Pular por agora, conectar depois
            </button>
          </div>
        )}

        {/* ================= PASSO 3: CONEXÃO GOOGLE CALENDAR ================= */}
        {step === 3 && (
          <div className="text-center">
            {/* INSERÇÃO DE MASCOTE */}
            <img src="7.png" alt="Finalizando" className="w-40 mx-auto mb-6 drop-shadow-lg" />

            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-100 px-4 py-2 rounded-full mb-6">
              <Star size={16} className="text-green-600" fill="#059669" />
              <span className="text-[11px] font-black text-green-600 uppercase tracking-wider">Último passo para o bônus</span>
            </div>

            <div className="w-24 h-24 bg-blue-50 text-[#4285F4] rounded-[30px] flex items-center justify-center mx-auto mb-8 -rotate-6 shadow-md shadow-blue-500/5">
              <ShieldCheck size={48} />
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase italic tracking-tight mb-4">Liberar Plataforma</h2>
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8">No último passo para ganhar seu bônus de 7 dias, realize a conexão com sua conta Google Agenda abaixo.</p>

            <button className="w-full h-14 bg-[#4285F4] text-white border-none rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all duration-400 hover:bg-blue-600 hover:-translate-y-0.5 active:scale-98 shadow-md" onClick={handleConnectGoogle} disabled={loading || ativandoTrial}>
              {loading || ativandoTrial ? <Loader2 className="animate-spin" size={20} /> : <><Calendar size={22} /> Conectar Agenda e Ativar</>}
            </button>
            <button className="w-full bg-transparent border-none text-slate-400 text-xs font-bold mt-5 cursor-pointer hover:text-slate-600 hover:underline disabled:opacity-50" onClick={handlePular} disabled={loading || ativandoTrial}>
              Pular por agora, conectar depois em Automação
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;